import { signOut } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { useUserStore } from "../store/userStore";
import axios from "axios";
import Swal from "sweetalert2";

const API_URL = import.meta.env.VITE_FIREBASE_API_URL;

// Flag global para evitar peticiones durante el logout
let _isLoggingOut = false;

export const isLoggingOut = (): boolean => _isLoggingOut;
export const setLoggingOut = (value: boolean): void => {
  _isLoggingOut = value;
};

export const decodeJWT = (token: string): any | null => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const expirationTime = decoded.exp * 1000;
  const now = Date.now();

  const bufferTime = 5 * 60 * 1000;
  return now >= expirationTime - bufferTime;
};

export const getTokenTimeRemaining = (token: string): number => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return 0;

  const expirationTime = decoded.exp * 1000;
  const remaining = expirationTime - Date.now();
  return remaining > 0 ? remaining : 0;
};

export const getTokenExpirationDate = (token: string): Date | null => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return null;
  return new Date(decoded.exp * 1000);
};

export const formatTimeRemaining = (token: string): string => {
  const remaining = getTokenTimeRemaining(token);
  if (remaining <= 0) return "Expirado";

  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const AUTH_STORAGE_KEYS = [
  "token",
  "sessionId",
  "lastRoute",
  "companyId",
  "userCompanyId",
  "userCompanyName",
  "userRole",
  "userRoleId",
  "userId",
  "userYardIds",
  "userYardId",
  "userYardName",
  "userGeofenceValidation",
  "vehicle-route-tracking",
  "vehicle-footprint-tracking",
  "tokenCFM",
  "tokenCFM_domain",
  "tokenCFM_userId",
] as const;

const ROLE_STORAGE_KEYS = ["userRole", "userRoleId"] as const;

export const clearAuthStorage = (): void =>
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

export const clearRoleStorage = (): void =>
  ROLE_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

export const clearSession = async (closeBackendSession = true) => {
  // Setear flag para evitar peticiones durante el logout
  _isLoggingOut = true;
  
  const { setUser } = useUserStore.getState();

  const token = localStorage.getItem("token");
  const sessionId = localStorage.getItem("sessionId");

  const fcmToken = localStorage.getItem("tokenCFM");
  const fcmUserId = localStorage.getItem("userId");
  if (closeBackendSession && token && sessionId && fcmToken && fcmUserId) {
    try {
      await axios.post(
        `${API_URL}/notifications/unsuscribe`,
        { tokenCFM: fcmToken, id: fcmUserId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-session-id": sessionId,
            "x-user-id": fcmUserId,
          },
        },
      );
    } catch (error) {
      console.error("Error unsubscribing FCM token on logout:", error);
    }
  }

  if (closeBackendSession && sessionId && token) {
    try {
      await axios.put(`${API_URL}/session/close`, null, {
        params: { id: sessionId, sessionId: sessionId },
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Error closing session in backend:", error);
    }
  }

  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out from Firebase:", error);
  }

  console.log("Limpiando localStorage desde clearSession");
  clearAuthStorage();

  setUser(null);
  
  // Nota: No reseteamos _isLoggingOut aquí porque queremos que 
  // permanezca true hasta que se redirija al login
};

export const forceLogout = async (redirect = true) => {
  await clearSession();

  if (redirect) {
    window.location.href = "/";
  }
};

/**
 * Logout forzado cuando la sesión se cierra remotamente
 * Muestra una alerta personalizable y redirige al login
 */
export const forceRemoteLogout = async (
  title: string = "Sesión cerrada",
  message: string = "Tu sesión fue cerrada de forma remota."
) => {
  if (isLoggingOut()) return;
  setLoggingOut(true);

  // Cerrar cualquier SweetAlert abierto
  Swal.close();

  // Limpiar sesión sin intentar llamar al backend
  await clearSession(false);

  // Sincronizar logout entre pestañas del mismo dispositivo
  try {
    new BroadcastChannel("auth").postMessage({ type: "session_closed" });
  } catch (error) {
    console.warn("BroadcastChannel not available:", error);
  }

  // Mostrar alerta
  await Swal.fire({
    icon: "warning",
    title: title,
    text: message,
    confirmButtonText: "Ir al Login",
    confirmButtonColor: "#16a34a",
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  // Redirigir al login
  window.location.href = "/";
};

export const checkTokenValidity = (): boolean => {
  const token = localStorage.getItem("token");

  if (!token) {
    return false;
  }

  if (isTokenExpired(token)) {
    clearSession();
    return false;
  }

  return true;
};

export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  return !isTokenExpired(token);
};

export const refreshToken = async (): Promise<boolean> => {
  const currentToken = localStorage.getItem("token");
  const sessionId = localStorage.getItem("sessionId");

  if (!currentToken) return false;

  try {
    // `{}` y no `null`: con Content-Type JSON, axios manda el texto «null» y el
    // body-parser de Functions Framework (anterior a nuestro Express, que sí lo
    // tolera desde #393) responde 400. Así fallaban 1043 de 1088 refresh en
    // prod (24 h al 26-oct): el token nunca se renovaba y la sesión vencía a
    // los 5 días aunque el usuario estuviera trabajando.
    const { data } = await axios.post(`${API_URL}/session/refresh`, {}, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
        "x-session-id": sessionId || "",
      },
    });

    const newToken =
      data?.data?.data?.token || data?.data?.token || data?.token;

    if (newToken) {
      localStorage.setItem("token", newToken);
      console.log("Token refreshed successfully");
      return true;
    }

    return false;
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    const key = axios.isAxiosError(error)
      ? (error.response?.data as any)?.meta?.messageKey
      : undefined;

    // Sesión cerrada remotamente o token inválido en el servidor
    if (
      status === 401 ||
      status === 403 ||
      key === "SESSION_CLOSED" ||
      key === "SESSION_NOT_FOUND"
    ) {
      console.warn("[refreshToken] Session invalid on server (SESSION_CLOSED):", key || status);
      return false; // El caller debe detectar esto y ejecutar logout
    }

    console.error("Error refreshing token:", error);
    return false;
  }
};

export const shouldRefreshToken = (token: string): boolean => {
  const timeRemaining = getTokenTimeRemaining(token);
  const oneDayInMs = 24 * 60 * 60 * 1000;
  return timeRemaining > 0 && timeRemaining < oneDayInMs;
};
